import useObservable from 'react-use/lib/useObservable';
import { makeStyles } from '@material-ui/core';
import BuildIcon from '@material-ui/icons/Build';
import CallMadeIcon from '@material-ui/icons/CallMade';
import HomeIcon from '@material-ui/icons/Home';
import LanguageIcon from '@material-ui/icons/Language';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ReceiptIcon from '@material-ui/icons/Receipt';
import Score from '@material-ui/icons/Score';
import SearchIcon from '@material-ui/icons/Search';
import StorageIcon from '@material-ui/icons/Storage';
import ScatterPlot from '@material-ui/icons/ScatterPlot';
import AccountTree from '@material-ui/icons/AccountTree';
import { mdiFolderKeyNetworkOutline } from '@mdi/js';
import {
  mdiBookshelf,
  mdiCircle,
  mdiKubernetes,
  mdiRocketLaunch,
  mdiShieldAccount,
  mdiSitemap,
  mdiTools,
  mdiDatabase,
  mdiFormatListBulleted,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  CatalogIcon,
  DocsIcon,
  Link,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  useSidebarOpenState,
} from '@backstage/core-components';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { Shortcuts } from '@backstage-community/plugin-shortcuts';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { InsightsSidebarItem } from '@spotify/backstage-plugin-insights';
import {
  FEATURE_FLAGS,
  RequireFlag,
} from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { environmentManagerAdminPermission } from 'backstage-plugin-neo4j-environment-manager';
import { imsControlPanelReadPermission } from '@internal/backstage-plugin-neo4j-ims-control-panel';
import { NoticesSidebarItem } from '@internal/backstage-plugin-neo4j-notices';
import {
  EnhancedSidebar,
  ExpandableSidebarGroup,
  SidebarFooter,
  ThemeToggle,
  SidebarIncidentLink,
  SIDEBAR_CHIP_MARGIN_TOP,
  SIDEBAR_CHIP_MARGIN_LEFT,
} from '@internal/plugin-neo4j-sidebar';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';

import { Logo } from './Logo';
import { SidebarMatrixRain } from './SidebarMatrixRain';

const AURA_MATRIX_THEME_ID = 'aura-matrix';
const AURA_MATRIX_SIDEBAR_LOGO_SRC = '/images/aura-portal-matrix.png';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  matrixLogoFull: {
    width: 135,
    height: 75,
    objectFit: 'contain',
    display: 'block',
  },
  matrixLogoCollapsed: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
  },
});

const useSidebarStyles = makeStyles(theme => {
  return {
    mdiIcon: {
      height: '1.25rem',
      width: '1.25rem',
    },
    importedMenuItemToSubmenu: {
      width: '100%',
      '& a': {
        width: '100%',
      },
      '& .MuiTypography-root': {
        fontSize: '0.8rem',
      },
      '& a > div:first-child': {
        width: 42,
        minWidth: 42,
        marginRight: 0,
      },
    },
    sidebarContainer: {
      '& nav .MuiBox-root > a[aria-current="page"]': {
        backgroundColor: 'transparent',
        boxShadow: `inset 3px 0 0 0 ${theme.palette.info.main}`,
        color: theme.palette.info.main,

        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },

      '& .sidebar-item-with-chip': {
        display: 'block',
        width: '100%',
        '& a': {
          '& .MuiTypography-root': {
            flex: '0 0 auto',
            width: 'auto',
          },
          '& > div:last-child': {
            width: 'auto',
            marginRight: 0,
            alignSelf: 'flex-start',
            marginTop: SIDEBAR_CHIP_MARGIN_TOP,
            marginLeft: SIDEBAR_CHIP_MARGIN_LEFT,
            '& span': { marginLeft: 0 },
          },
        },
      },
      '& hr': {
        backgroundColor: theme.palette.divider,
        border: 'none',
        height: '1px',
      },
    },
  };
});

function SidebarLogo() {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );
  const isMatrixTheme = themeId === AURA_MATRIX_THEME_ID;

  let logo = null;
  if (isMatrixTheme) {
    logo = (
      <img
        src={AURA_MATRIX_SIDEBAR_LOGO_SRC}
        alt="Aura Portal"
        className={
          isOpen ? classes.matrixLogoFull : classes.matrixLogoCollapsed
        }
      />
    );
  } else {
    logo = <Logo isOpen={isOpen} />;
  }

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {logo}
      </Link>
    </div>
  );
}

// NFS passes navItems from registered NavItemBlueprints, but this sidebar is fully hand-rolled
// with custom permission/flag wrapping — add new sidebar entries explicitly below.
// Keep the entries inside each group in A-Z order, and write each label in sentence case.
export function SidebarContent({
  navItems: _navItems,
}: NavContentComponentProps) {
  const classes = useSidebarStyles();
  const DatabaseIcon = () => (
    <Icon path={mdiDatabase} className={classes.mdiIcon} />
  );

  return (
    <div className={classes.sidebarContainer}>
      <EnhancedSidebar openDelayMs={0}>
        <SidebarLogo />
        <SidebarDivider />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal
            resultItemComponents={[
              <CatalogSearchResultListItem icon={<CatalogIcon />} />,
              <TechDocsSearchResultListItem icon={<DocsIcon />} />,
            ]}
          />
        </SidebarGroup>

        <SidebarDivider />

        <SidebarItem icon={HomeIcon} to="/" text="Home" />

        <ExpandableSidebarGroup
          icon={<Icon path={mdiCircle} className={classes.mdiIcon} />}
          label="Omni"
          id="omni"
        >
          <SidebarItem icon={CallMadeIcon} to="create" text="Actions" />
          <RequirePermission
            permission={environmentManagerAdminPermission}
            errorPage={<></>}
          >
            <SidebarItem
              icon={LanguageIcon}
              to="/environments"
              text="Environments"
            />
          </RequirePermission>
          <RequirePermission
            permission={imsControlPanelReadPermission}
            errorPage={<></>}
          >
            <SidebarItem
              icon={() => <Icon path={mdiSitemap} size="20px" />}
              to="/ims-control-panel"
              text="IMS control panel"
            />
          </RequirePermission>
          <SidebarItem
            icon={() => <Icon path={mdiKubernetes} size="20px" />}
            to="/k8s-clusters"
            text="K8s clusters"
          />
          <RequireFlag flag={FEATURE_FLAGS.ROLLOUTS}>
            <SidebarItem
              icon={() => <Icon path={mdiRocketLaunch} size="20px" />}
              to="/rollouts"
              text="Rollouts"
            />
          </RequireFlag>
        </ExpandableSidebarGroup>

        <ExpandableSidebarGroup
          icon={<Icon path={mdiBookshelf} className={classes.mdiIcon} />}
          label="Resources"
          id="resources"
        >
          <SidebarItem icon={StorageIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={MenuBookIcon} to="docs" text="Documentation" />
          <SidebarItem icon={Score} to="excellence" text="Excellence" />
        </ExpandableSidebarGroup>

        <ExpandableSidebarGroup
          icon={<Icon path={mdiTools} className={classes.mdiIcon} />}
          label="Product"
          id="product"
        >
          <SidebarItem icon={ReceiptIcon} to="/billing" text="Billing" />
          <RequireFlag flag={FEATURE_FLAGS.COMPOSITE}>
            <SidebarItem
              icon={AccountTree}
              to="/composites"
              text="Composites"
            />
          </RequireFlag>
          <SidebarItem
            icon={ScatterPlot}
            to="/graph-analytics"
            text="Graph analytics"
          />
          <RequireFlag flag={FEATURE_FLAGS.GRAPH_ENGINE}>
            <SidebarItem
              icon={ScatterPlot}
              to="/graph-engines"
              text="Graph engines"
            />
          </RequireFlag>
          <SidebarItem
            icon={DatabaseIcon}
            to="/neo4j-clusters"
            text="Neo4j clusters"
          />
          <SidebarItem
            icon={DatabaseIcon}
            to="/neo4j-databases"
            text="Neo4j databases"
          />
          <SidebarItem
            icon={() => <Icon path={mdiFormatListBulleted} size={1} />}
            to="/aura-operations"
            text="Operations audit"
          />
          <RequireFlag flag={FEATURE_FLAGS.PRIVATE_ENDPOINT_SERVICE}>
            <SidebarItem
              icon={() => <Icon path={mdiFolderKeyNetworkOutline} size={1} />}
              to="/private-endpoints-overview"
              text="Private endpoints"
            />
          </RequireFlag>
        </ExpandableSidebarGroup>

        <SidebarDivider />

        <SidebarIncidentLink />

        <SidebarDivider />

        <SidebarScrollWrapper>
          <Shortcuts allowExternalLinks />
        </SidebarScrollWrapper>

        <SidebarFooter>
          <ExpandableSidebarGroup
            icon={<Icon path={mdiShieldAccount} className={classes.mdiIcon} />}
            label="Admin"
            id="admin"
          >
            <RequirePermission
              permission={devToolsAdministerPermission}
              errorPage={<></>}
            >
              <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
            </RequirePermission>
            <div className={classes.importedMenuItemToSubmenu}>
              <InsightsSidebarItem />
            </div>
            <NoticesSidebarItem />
          </ExpandableSidebarGroup>

          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>

          <ThemeToggle />
        </SidebarFooter>

        <SidebarMatrixRain />
      </EnhancedSidebar>
    </div>
  );
}
